import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/lib/apiClient";
import { Siren, X, MapPin, ShieldAlert } from "lucide-react";

const ago = (iso) => {
  const d = Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
};

export function OutbreakAlerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);

  useEffect(() => { apiClient.get("/alerts/outbreaks").then((r) => setAlerts(r.data)).catch(() => {}); }, []);

  const dismiss = async (a) => {
    setAlerts((as) => as.filter((x) => x.key !== a.key));
    try { await apiClient.post("/alerts/dismiss", { key: a.key }); } catch {}
  };

  if (!alerts.length) return null;
  return (
    <div className="mb-8 space-y-3" data-testid="outbreak-alerts">
      {alerts.map((a) => {
        const high = (a.severity || "").toLowerCase() === "high";
        return (
          <div key={a.key} data-testid={`outbreak-alert-${a.key.replace(/[^a-z0-9]/gi, "-")}`}
            className={`rounded-2xl p-4 sm:p-5 flex gap-4 items-start border ${high ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${high ? "bg-red-600" : "bg-amber-500"} text-white`}><Siren className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white ${high ? "bg-red-600" : "bg-amber-500"}`}>{a.severity} · nearby outbreak</span>
                <span className="text-xs text-stone-500">{ago(a.last_seen)}</span>
              </div>
              <p className="font-display font-bold text-emerald-950 mt-1"><span data-testid="alert-problem">{a.problem}</span> on {a.crop} seen {a.distance_km} km from <b>{a.field_name}</b>{a.count > 1 ? ` (${a.count} reports)` : ""}</p>
              <p className="text-sm text-stone-600 mt-0.5">You grow {a.crop} there — check your plants this week and act early.</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button data-testid="alert-map-btn" onClick={() => navigate("/app/map")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2D6A4F] bg-white border border-stone-200 rounded-full px-3 py-1.5 hover:border-[#2D6A4F]"><MapPin className="h-3.5 w-3.5" /> See on map</button>
                <button data-testid="alert-pest-btn" onClick={() => navigate("/app/pest")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#78350F] bg-white border border-stone-200 rounded-full px-3 py-1.5 hover:border-[#78350F]"><ShieldAlert className="h-3.5 w-3.5" /> Check my plants</button>
              </div>
            </div>
            <button data-testid="alert-dismiss-btn" onClick={() => dismiss(a)} className="p-1.5 rounded-lg hover:bg-white/60 text-stone-500 shrink-0" title="Dismiss"><X className="h-4 w-4" /></button>
          </div>
        );
      })}
    </div>
  );
}
